/**
 * usePosterGenerator — thin wrapper around the pse-pipeline-v3 worker endpoints.
 *   - GET  /api/v1/styles
 *   - POST /api/v1/generations
 *   - GET  /api/v1/generations/:jobId
 *
 * All calls use the current user's JWT from AuthContext. Errors carry `.status`
 * + `.data` for callers to distinguish 401 / 402-shaped responses.
 */
import { API_BASE as BV_API } from '../config.js';
import { useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

// Brightivity: brightivity-posters behind the front door (the pipeline's submit/poll/styles surface).
const API_BASE = `${BV_API}/posters`;
const SUBMIT_PATH = '/generations';

export default function usePosterGenerator() {
  const { user } = useAuth();
  const token = user?.token || null;

  const apiFetch = useCallback(async (path, options = {}) => {
    if (!token) throw new Error('Not signed in');
    const headers = { ...(options.headers || {}) };
    headers['Authorization'] = `Bearer ${token}`;
    if (options.body && !headers['Content-Type']) headers['Content-Type'] = 'application/json';

    const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
    let json;
    try { json = await res.json(); } catch { json = null; }

    if (!res.ok) {
      const err = new Error(json?.message || json?.error || `HTTP ${res.status}`);
      err.status = res.status;
      err.data = json?.data ?? null;
      err.body = json;
      throw err;
    }
    // Worker responses come wrapped as { status, data } — unwrap
    return json?.data ?? json;
  }, [token]);

  const fetchStyles = useCallback(async ({ subject, grade, limit = 12 } = {}) => {
    const params = new URLSearchParams();
    if (subject) params.set('subject', subject);
    if (grade != null) params.set('grade', String(grade));
    if (limit) params.set('limit', String(limit));
    const data = await apiFetch(`/styles?${params.toString()}`);
    return data?.styles || [];
  }, [apiFetch]);

  const submitGeneration = useCallback(async (payload) => {
    return await apiFetch(SUBMIT_PATH, {
      method: 'POST',
      // Routes this submission to the IONOS studioCore daemon instead of the
      // legacy Cloudflare DO container. The worker reads this header at submit
      // time and tags the queue row accordingly. v1.0.7- builds (without this
      // header) keep using the CF container.
      // Brightivity: no X-Pipeline-Executor header (the front door's CORS only allows Content-Type and
      // Authorization) — brightivity-posters sets executor 'ionos' on every submit itself.
      body: JSON.stringify(payload),
    });
  }, [apiFetch]);

  const pollJob = useCallback(async (jobId) => {
    return await apiFetch(`/generations/${jobId}`);
  }, [apiFetch]);

  // Client-side "I gave up" signal. Backend flips queue row to `cancelled`
  // and the sweeper's Pass 2 refund pass picks it up within ~60 s. Best-
  // effort: if the call fails, the timeout UI still fires — the user
  // already saw the failure, no point blocking on a network round-trip.
  const abandonJob = useCallback(async (jobId) => {
    try {
      return await apiFetch(`/generations/${jobId}/abandon`, { method: 'POST' });
    } catch (err) {
      console.warn('[generator] abandonJob failed', err);
      return null;
    }
  }, [apiFetch]);

  return { fetchStyles, submitGeneration, pollJob, abandonJob, apiBase: API_BASE };
}
